import React, { useEffect, useState } from "react";
import {
  useGetAssessmentReports,
  useGetVQAActivity,
  useGetAssesmentRequestDetails,
  useDocumentFile,
  useDocumentAllFiles,
} from "../../custom-hooks/queries";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowDownTrayIcon,
  CheckBadgeIcon,
  ChevronRightIcon,
  FolderPlusIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import Skeleton from "react-loading-skeleton";
import { dateFormat, calculateDaysAgo } from "../../utils/helperMethods";
import StatusProgressBar from "../../components/ProgressBar";
import CustomButton from "../../components/CustomButton";
import Modal from "../../components/Modal";
import Pagination from "../../components/CustomPagination";
import StatusForDrawer from "../../components/StatusForDrawer";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { ReportContainer } from "../../components/ReportContainer";
import Capabilities from "../../components/Capabilities";

export const AssesmentRequestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { datePassed } = location.state || {};

  const [currentPage, setCurrentPage] = useState(1);

  const { data: assessmentDetails, isLoading } =
    useGetAssesmentRequestDetails(id);
  const { data: activity, refetch: activityRefetch } = useGetVQAActivity(
    assessmentDetails?.vqa_id
  );
  const [message, setMessage] = useState("");
  const [show, setShow] = useState(false);

  const {
    data: allDocuments,
    Loading,
    isError,
    error,
  } = useGetAssessmentReports(
    assessmentDetails?.vqa_id
  );

  const [comment, setComment] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [isRequested, setIsRequested] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalDescription, setModalDescription] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    // Replace this with your actual logic to check if user is admin
    // Example: setIsAdmin(authContext.user?.role === 'admin');
    const userRole = localStorage.getItem("userRole");
    setIsAdmin(userRole === "admin");
  }, []);

  useEffect(() => {
    activityRefetch();
  }, [assessmentDetails]);

  // Handler for closing the modal
  const handleModalClose = () => {
    navigate("/RequestAssesmentList");
  };

  const {
    mutate,
    data: files,
    isError: isFileError,
    error: errorMessageFile,
  } = useDocumentFile();
  const {
    mutate: allFilesDownload,
    data: allFiles,
    isError: allFilesError,
  } = useDocumentAllFiles();

  const handleDownload = (id) => {
    mutate(id, {
      onSuccess: () => {
        setShow(true);
        setMessage("Downloaded Successfully");
      },
      onError: () => {
        setShow(true);
        setMessage("Download Failed");
      },
    });
  };

  const handleDownloadAll = async (ids) => {
    allFilesDownload(ids); // Pass ids directly instead of using docsIds (state update is async)
  };

  const hasCancelledAssessment =
    assessmentDetails?.join_vqa_request_status?.includes("Cancel");

  let filteredReports = hasCancelledAssessment
    ? allDocuments?.items?.filter(
        (document) =>
          document?.document_sub_type !== "Report Anonymized" &&
          document?.document_sub_type !== "CAPA Response Report"
      ) || []
    : allDocuments?.items || [];

  // Apply isAdmin condition independently
  if (!isAdmin) {
    filteredReports = filteredReports.filter(
      (document) => document?.document_sub_type !== "Report Anonymized"
    );
  }

  return (
    <div className="container">
      {isLoading ? (
        <Skeleton
          className="w-full h-32 p-7 border-y border-gray-300 bg-slate-50 mb-10"
          enableAnimation={true}
        />
      ) : (
        <div className="p-7 border-y border-gray-300 bg-slate-50 mb-10">
          <div className="flex items-center text-sm">
            <h1
              className="text-gray-500 cursor-pointer font-semibold"
              onClick={() => navigate("/RequestAssesmentList")}
            >
              Shared Assessments
            </h1>
            <ChevronRightIcon className="w-5 h-5 text-gray-400" />
          </div>
          <div className="mt-5 space-y-2 flex  flex-col justify-start items-start md:justify-between md:items-center md:flex-row">
            <div className="flex items-center">
              <span className="bg-white border border-gray-300 rounded-md px-7 py-5 mr-5">
                <CheckBadgeIcon className="h-9 w-9 text-red-500" />
              </span>

              <span>
                <p className="text-sm text-gray-500">
                  {assessmentDetails?.provider_name}
                </p>

                <p className="text-sm text-gray-500 ">
                  Request Date:{" "}
                  <span className="font-semibold">
                    {assessmentDetails?.vqa === null
                      ? dateFormat(assessmentDetails?.vqa_created_on)
                      : dateFormat(assessmentDetails?.join_vqa_request_created_on)}
                  </span>
                </p>
              </span>
            </div>
            {
              <CustomButton
                text={assessmentDetails?.join_vqa_request_status || "-"}
                width="w-[120px]"
                height="h-10"
                weight="font-semibold"
                size="text-md"
                color={
                  assessmentDetails?.join_vqa_request_status === "Rejected" ||
                  assessmentDetails?.join_vqa_request_status === "Cancelled"
                    ? "bg-red-400"
                    : "bg-indigo-600"
                }
                textColor="text-white"
                borderRadius="rounded-md"
                border="none"
                disabled={true}
              />
            }
          </div>
          {datePassed && (
            <div className="mt-4 p-3 bg-red-100 border border-red-400 rounded text-red-700">
              Date to join this VQA has passed.
            </div>
          )}

          <div className="mt-5 flex flex-col md:flex-row md:justify-between items-start md:items-center space-y-2"></div>
        </div>
      )}

      <div className="mb-28 px-5 flex justify-center md:justify-between items-stretch flex-col md:flex-row">
        {isLoading ? (
          <Skeleton
            className="w-full md:w-[100%] h-64"
            enableAnimation={true}
          />
        ) : (
          <>
            {/* Left column: Join Request Details */}
            <div className="w-full md:w-[90%] border-r border-gray-300 px-3 mr-8">
              <div className="border-b border-gray-300 pb-2">
                <h1 className="text-sm font-medium text-gray-900">
                  Join Request Details
                </h1>
              </div>

              <div className="my-5">
                {assessmentDetails?.join_vqa_request_status && (
                  <StatusProgressBar
                    status={
                      assessmentDetails?.join_vqa_request_status === "Cancel"
                        ? assessmentDetails?.join_vqa_request_status
                        : assessmentDetails.vqa_status
                    }
                    vqa={assessmentDetails?.vqa ?? assessmentDetails}
                  />
                )}
              </div>

              <div className="divide-y divide-gray-300">
                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">Scope</h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    <Capabilities
                      services={assessmentDetails?.capabilities}
                      icon={true}
                      showLimited={false}
                    />
                  </p>
                </div>
                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Additional Info
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {assessmentDetails?.additional_info || "-"}
                  </p>
                </div>

                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Sponsor Name
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {assessmentDetails?.sponsor?.sponsor_name || "-"}
                  </p>
                </div>

                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Sponsor Contact Name
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {assessmentDetails?.sponsor?.sponsor_contact_name || "-"}
                  </p>
                </div>
                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Sponsor Contact Email
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {assessmentDetails?.sponsor?.sponsor_contact_email || "-"}
                  </p>
                </div>
                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Sponsor Contact Number
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {assessmentDetails?.sponsor?.sponsor_contact_phone || "-"}
                  </p>
                </div>

                <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                  <h2 className="text-sm font-medium text-gray-900">
                    Primary Sponsor
                  </h2>
                  <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                    {assessmentDetails?.sponsor?.is_primary_sponsor
                      ? "Yes"
                      : "No"}
                  </p>
                </div>
              </div>
            </div>

            {/* Right column: Reports and Assessment Activity */}
            <div className="w-full md:w-[30%] mt-10 md:mt-0">
              <ReportContainer
                documents={filteredReports}
                status={assessmentDetails?.join_vqa_request_status}
                vendorName={assessmentDetails?.provider_name}
              />

              <div className="mt-10 md:mt-6">
                <h2 className="text-sm font-semibold text-gray-900">
                  Assessment Activity
                </h2>
                <ul className="mt-6 space-y-6 h-80 overflow-y-auto px-2">
                  {assessmentDetails?.vqa !== null &&
                    activity?.items
                      ?.sort(
                        (a, b) =>
                          new Date(b.activity_done_on) -
                          new Date(a.activity_done_on)
                      )
                      ?.map((item, index, array) => {
                        // Get the latest activity (most recent date)
                        const isLatest =
                          item?.activity_done_on ===
                          array.reduce((latest, current) =>
                            new Date(current?.activity_done_on) >=
                            new Date(latest?.activity_done_on)
                              ? current
                              : latest
                          )?.activity_done_on;

                        // Format date as 'Month DD, YYYY'
                        const formattedDate = item?.activity_done_on
                          ? new Date(item.activity_done_on).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              }
                            )
                          : "-";

                        return (
                          <li key={index} className="relative flex gap-x-4">
                            {/* Line connecting items */}
                            <div className="absolute left-0 top-0 flex w-6 justify-center -bottom-6">
                              <div className="w-px bg-gray-200"></div>
                            </div>

                            {/* Circle or Profile Picture */}
                            <div className="relative flex h-6 w-6 items-center justify-center bg-white">
                              {item?.activity_comments && !isLatest ? (
                                <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center ring-2 ring-gray-300">
                                  <span className="text-xs font-medium text-gray-700">
                                    {item?.activity_done_by
                                      ?.charAt(0)
                                      .toUpperCase() || "A"}
                                  </span>
                                </div>
                              ) : (
                                <div className="absolute right-0 bottom-0 text-xs text-green-500">
                                  <CheckCircleIcon className="size-6 absolute right-0 bottom-0 text-green-600" />
                                </div>
                              )}
                            </div>

                            {/* Activity Description and Comment */}
                            <div className="flex-[1_1_0%]">
                              <p className="py-0.5 text-xs text-gray-500">
                                <span className="font-medium text-gray-900">
                                  {!item?.activity_comments &&
                                    item?.activity_done_by}
                                </span>{" "}
                                {item?.activity_description}
                              </p>
                              {item?.activity_comments && (
                                <div className="mt-2 rounded-md border border-gray-300 p-3 text-sm text-gray-800 shadow-sm w-[110%]">
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-900 text-xs">
                                      {item?.activity_done_by}{" "}
                                      <span className="py-0.5 text-xs leading-5 text-gray-500 font-normal">
                                        commented
                                      </span>
                                    </span>
                                    <time
                                      dateTime={item?.activity_done_on}
                                      className="flex-none py-0.5 text-xs text-gray-500"
                                    >
                                      {formattedDate}
                                    </time>
                                  </div>
                                  <div className="text-gray-500 text-sm mt-2">
                                    {item?.activity_comments}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Activity Time */}
                            <time
                              dateTime={item?.activity_done_on}
                              className="flex-none py-0.5 text-xs text-gray-500"
                            >
                              {!item?.activity_comments && formattedDate}
                            </time>
                          </li>
                        );
                      })}
                </ul>
              </div>
            </div>
          </>
        )}
      </div>

      <Modal
        open={openModal}
        setOpen={setOpenModal}
        onClick={handleModalClose}
        is_requested={isRequested}
        title={modalTitle}
        description={modalDescription}
        textBtn="Assesments Request List"
        vqaDetailsRefetch={handleModalClose}
      />
    </div>
  );
};

export default AssesmentRequestDetails;
